import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

/**
 * Mounts the shared chat widget (the same bundle customers embed on their own
 * websites) in the portal, so there is one chat window rather than two.
 *
 * The bundle is served by this image at /chat-widget.umd.js and renders itself
 * into a shadow root, so nothing is rendered here. If it cannot be loaded the
 * caller is told, and the portal keeps its own chat component.
 */

declare global {
  interface Window {
    CentraOnePlusWidget?: {
      init: (config: ChatWidgetConfig) => ChatWidgetApi;
      destroy: () => void;
      api?: ChatWidgetApi;
    };
    __CENTRAONEPLUS_WIDGET_CSS__?: string;
  }
}

interface WidgetVisitor {
  id?: string;
  name?: string;
  email?: string;
}

interface ChatWidgetConfig {
  tenantId: string;
  apiUrl?: string;
  wsUrl?: string;
  position?: 'bottom-right' | 'bottom-left';
  welcomeMessage?: string;
  visitor?: WidgetVisitor;
  theme?: { primaryColor?: string; headerColor?: string; headerTextColor?: string };
  preChatForm?: {
    enabled: boolean;
    verifyEmail?: boolean;
    title?: string;
    submitButtonText?: string;
    fields: Array<{
      id: string;
      type: 'text' | 'email' | 'phone' | 'textarea';
      label: string;
      placeholder?: string;
      required?: boolean;
    }>;
  };
}

interface ChatWidgetApi {
  open: () => void;
  close: () => void;
  toggle: () => void;
  setVisitorInfo: (info: WidgetVisitor) => void;
  identify: (visitor: WidgetVisitor | null) => Promise<void>;
  destroy: () => void;
}

interface ChatWidgetLoaderProps {
  tenantId: string;
  welcomeMessage?: string;
  primaryColor?: string;
  /** Called when the widget bundle cannot be used, so the portal can fall back. */
  onUnavailable?: () => void;
}

const WIDGET_SCRIPT = '/chat-widget.umd.js';

/** The signed-in customer, or nobody. `id` scopes the stored chat to them. */
function visitorFor(
  isAuthenticated: boolean,
  customer: { id?: string; name?: string; email?: string } | null,
): WidgetVisitor | undefined {
  if (!isAuthenticated || !customer) return undefined;
  return { id: customer.id || customer.email, name: customer.name, email: customer.email };
}

export function ChatWidgetLoader({
  tenantId,
  welcomeMessage,
  primaryColor,
  onUnavailable,
}: ChatWidgetLoaderProps) {
  const { customer, isAuthenticated } = useAuthStore();
  const [ready, setReady] = useState(false);
  const initialized = useRef(false);
  const unavailable = useRef(onUnavailable);
  unavailable.current = onUnavailable;

  // Load and start the widget once. Identity changes are handled below, so a
  // sign-in does not tear the chat down and lose the conversation on screen.
  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    let cancelled = false;

    const start = () => {
      if (cancelled || initialized.current || !window.CentraOnePlusWidget) return;
      initialized.current = true;
      window.CentraOnePlusWidget.init({
        tenantId,
        // Same-origin: nginx proxies /api to the BFF.
        apiUrl: '/api',
        wsUrl: import.meta.env.VITE_CHANNEL_WS_URL || 'https://dev-channel-gateway.centraone.ai',
        position: 'bottom-right',
        welcomeMessage,
        visitor: visitorFor(isAuthenticated, customer),
        theme: primaryColor ? { primaryColor } : undefined,
        // A guest gives their details and verifies the email, as the portal
        // chat has always done. A signed-in customer is asked for nothing.
        preChatForm: isAuthenticated
          ? undefined
          : {
              enabled: true,
              verifyEmail: true,
              title: 'Before we start',
              submitButtonText: 'Start chat',
              fields: [
                { id: 'name', type: 'text', label: 'Your Name', placeholder: 'Your name', required: true },
                { id: 'email', type: 'email', label: 'Email Address', placeholder: 'you@example.com', required: true },
              ],
            },
      });
      setReady(true);
    };

    // nginx serves the SPA for unknown paths, so a missing bundle would arrive
    // as HTML and throw a SyntaxError. Check what it is before loading it.
    fetch(WIDGET_SCRIPT, { method: 'HEAD' })
      .then((res) => {
        if (cancelled) return;
        const contentType = res.headers.get('content-type') || '';
        if (!res.ok || !contentType.includes('javascript')) {
          unavailable.current?.();
          return;
        }
        script = document.createElement('script');
        script.src = WIDGET_SCRIPT;
        script.async = true;
        script.onload = start;
        script.onerror = () => unavailable.current?.();
        document.body.appendChild(script);
      })
      .catch(() => {
        if (!cancelled) unavailable.current?.();
      });

    return () => {
      cancelled = true;
      if (initialized.current) {
        window.CentraOnePlusWidget?.destroy();
        initialized.current = false;
      }
      script?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Signing in, signing out or a different customer on this browser: the widget
  // starts their own chat rather than showing the previous person's.
  useEffect(() => {
    if (!ready) return;
    void window.CentraOnePlusWidget?.api?.identify(visitorFor(isAuthenticated, customer) ?? null);
  }, [ready, isAuthenticated, customer]);

  return null;
}
